import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import XpnsrPageHeader from './XpnsrPageHeader';
import { RefreshCw, Server, Database, Activity, Cpu, Clock, DollarSign, Shield } from 'lucide-react';

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const fmtBytes = (n) => {
  if (n === null || n === undefined) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = Number(n);
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

function StatCard({ icon: Icon, title, children }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {Icon ? <Icon className="w-4 h-4 text-primary" /> : null}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">{children}</CardContent>
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

export default function SystemStatus() {
  const { t } = useT();
  const [sys, setSys] = useState(null);
  const [runtime, setRuntime] = useState(null);
  const [fx, setFx] = useState(null);
  const [torText, setTorText] = useState('');
  const [torSaving, setTorSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [sysRes, rtRes, fxRes] = await Promise.allSettled([
        axios.get(API + '/admin/system'),
        axios.get(API + '/admin/runtime'),
        axios.get(API + '/admin/fx-rates'),
      ]);
      if (sysRes.status === 'fulfilled') {
        setSys(sysRes.value.data);
      } else {
        throw sysRes.reason;
      }
      setRuntime(rtRes.status === 'fulfilled' ? rtRes.value.data : null);
      setFx(fxRes.status === 'fulfilled' ? fxRes.value.data : null);
    } catch (err) {
      console.error('Error loading system status:', err);
      setError(err?.response?.data?.detail || t('Error loading system status'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const refreshTorExits = async () => {
    if (!torText.trim()) {
      toast.error(t('Paste the Tor exit list first'));
      return;
    }
    try {
      setTorSaving(true);
      const res = await axios.post(API + '/admin/ip-intelligence/tor-exits', { exits: torText });
      const n = res.data?.count ?? res.data?.loaded ?? '';
      toast.success(t('Tor exit list updated') + (n !== '' ? ` (${n})` : ''));
      setTorText('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('Failed to update Tor exit list'));
    } finally {
      setTorSaving(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const proc = sys?.process || {};
  const db = sys?.database || {};
  const dp = sys?.data_processing || {};
  const host = sys?.host || {};
  const cron = sys?.cron || {};
  const counts = db.collection_counts || {};

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Admin')}
        title={t('System status')}
        subtitle={t('Process uptime, database size, data-processing queue and host metrics.')}
        actions={
          <Button variant="outline" onClick={fetchAll} disabled={loading}>
            <RefreshCw className={'w-4 h-4 mr-2 ' + (loading ? 'animate-spin' : '')} />
            {t('Refresh')}
          </Button>
        }
      />

      {error ? (
        <Card><CardContent className="py-8 text-center text-rose-600">{error}</CardContent></Card>
      ) : loading && !sys ? (
        <div className="skeleton h-96 w-full"></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard icon={Server} title={t('Process')}>
            <Row label={t('Status')} value={<Badge variant="secondary">{sys?.status || 'ok'}</Badge>} />
            <Row label={t('Uptime')} value={proc.uptime_human || '—'} />
            <Row label={t('Started')} value={fmtTime(proc.start_at)} />
            <Row label={t('Python')} value={proc.python || '—'} />
            <Row label={t('Platform')} value={<span className="text-xs">{proc.platform || '—'}</span>} />
          </StatCard>

          <StatCard icon={Database} title={t('Database')}>
            <Row label={t('Total documents')} value={(db.total_documents ?? 0).toLocaleString()} />
            {Object.keys(counts).map((k) => (
              <Row key={k} label={k} value={(counts[k] ?? 0).toLocaleString()} />
            ))}
          </StatCard>

          <StatCard icon={Activity} title={t('Data processing')}>
            <Row label={t('Analytics backend')} value={<Badge variant="outline">{dp.analytics_backend || 'mongo'}</Badge>} />
            <Row label={t('Ingest mode')} value={dp.ingest?.mode || '—'} />
            <Row label={t('Queue depth')} value={dp.queue_depth ?? '—'} />
            {runtime ? (
              <>
                <Row label={t('Click uniqueness')} value={runtime.click_uniqueness?.backend || '—'} />
                <Row label={t('Redis ping')} value={runtime.edge_ingest?.redis_ping ? 'ok' : '—'} />
              </>
            ) : null}
          </StatCard>

          <StatCard icon={Cpu} title={t('Host')}>
            <Row label={t('Load average')} value={host.load_average ? host.load_average.join(' · ') : '—'} />
            <Row label={t('Disk used')} value={host.disk ? `${fmtBytes(host.disk.used_bytes)} / ${fmtBytes(host.disk.total_bytes)} (${host.disk.used_percent ?? '—'}%)` : '—'} />
            <Row label={t('Disk free')} value={host.disk ? fmtBytes(host.disk.free_bytes) : '—'} />
          </StatCard>

          <StatCard icon={Clock} title={t('Cron')}>
            <Row label={t('Last cost sync')} value={fmtTime(cron.last_cost_sync_at)} />
            <Row label={t('Generated at')} value={fmtTime(sys?.generated_at)} />
          </StatCard>

          <StatCard icon={DollarSign} title={t('FX rates')}>
            {fx && (fx.rates || fx.base) ? (
              <>
                <Row label={t('Base')} value={fx.base || 'USD'} />
                <Row label={t('Updated')} value={fmtTime(fx.refreshed_at || fx.updated_at || fx.fetched_at)} />
                {Object.entries(fx.rates || {}).slice(0, 10).map(([k, v]) => (
                  <Row key={k} label={k} value={typeof v === 'number' ? v : String(v)} />
                ))}
              </>
            ) : (
              <p className="text-muted-foreground">{t('No FX rates cached.')}</p>
            )}
          </StatCard>

          <StatCard icon={Shield} title={t('Tor exit list')}>
            <p className="text-muted-foreground">
              {t('Paste a newline-delimited Tor exit-node dump (e.g. from torbulkexitlist) to refresh the in-memory block list.')}
            </p>
            <Textarea
              rows={4}
              value={torText}
              onChange={(e) => setTorText(e.target.value)}
              placeholder={'1.2.3.4\n5.6.7.8'}
              className="font-mono text-xs"
            />
            <Button size="sm" onClick={refreshTorExits} disabled={torSaving}>
              {torSaving ? t('Saving…') : t('Replace list')}
            </Button>
          </StatCard>
        </div>
      )}
    </div>
  );
}
